import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Centralized polling controller for all dashboard data fetching
 * Manages page visibility, user presence, and polling decisions
 * Single source of truth for when polling should occur
 */
export const usePollingController = () => {
  // Page visibility state
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden)
  
  // User presence state
  const [userPresenceState, setUserPresenceState] = useState({
    confirmed: true,
    needsConfirmation: false,
    lastInteraction: Date.now(),
    presenceTimer: null,
    confirmationTimer: null,
  })
  
  const mountedRef = useRef(true)
  const interactionThrottleRef = useRef(0)
  
  // Page visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (mountedRef.current) {
        setIsPageVisible(!document.hidden)
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])
  
  // User interaction detection with throttling
  const resetPresenceTimer = useCallback(() => {
    const now = Date.now()
    
    // Throttle to max 1 reset per 30 seconds
    if (now - interactionThrottleRef.current < 30000) {
      return
    }
    
    interactionThrottleRef.current = now
    
    setUserPresenceState(prev => {
      // Clear existing timers
      if (prev.presenceTimer) clearTimeout(prev.presenceTimer)
      if (prev.confirmationTimer) clearTimeout(prev.confirmationTimer)
      
      // Set new presence timer (10 minutes)
      const presenceTimer = setTimeout(() => {
        if (mountedRef.current) {
          setUserPresenceState(current => ({
            ...current,
            needsConfirmation: true,
            confirmed: false,
            presenceTimer: null,
            // Set confirmation timeout (2 minutes)
            confirmationTimer: setTimeout(() => {
              if (mountedRef.current) {
                setUserPresenceState(final => ({
                  ...final,
                  needsConfirmation: false,
                  confirmed: false,
                  confirmationTimer: null,
                }))
              }
            }, 2 * 60 * 1000),
          }))
        }
      }, 10 * 60 * 1000)
      
      return {
        ...prev,
        confirmed: true,
        needsConfirmation: false,
        lastInteraction: now,
        presenceTimer,
        confirmationTimer: null,
      }
    })
  }, [])
  
  // User interaction listeners
  useEffect(() => {
    const events = ['click', 'scroll', 'keypress', 'mousemove']
    
    events.forEach(event => {
      document.addEventListener(event, resetPresenceTimer, { passive: true })
    })
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetPresenceTimer)
      })
    }
  }, [resetPresenceTimer])
  
  // Initialize presence timer on mount
  useEffect(() => {
    mountedRef.current = true
    resetPresenceTimer()
    
    return () => {
      mountedRef.current = false
      setUserPresenceState(prev => {
        if (prev.presenceTimer) clearTimeout(prev.presenceTimer)
        if (prev.confirmationTimer) clearTimeout(prev.confirmationTimer)
        return prev
      })
    }
  }, [resetPresenceTimer])
  
  // Manual presence confirmation
  const confirmPresence = useCallback(() => {
    resetPresenceTimer()
  }, [resetPresenceTimer])
  
  // Pause updates (user clicked "Pause updates")
  const pauseUpdates = useCallback(() => {
    setUserPresenceState(prev => {
      if (prev.presenceTimer) clearTimeout(prev.presenceTimer)
      if (prev.confirmationTimer) clearTimeout(prev.confirmationTimer)
      
      return {
        ...prev,
        confirmed: false,
        needsConfirmation: false,
        presenceTimer: null,
        confirmationTimer: null,
      }
    })
  }, [])
  
  // Master polling decision
  const shouldPoll = isPageVisible && userPresenceState.confirmed
  
  return {
    shouldPoll,
    isPageVisible,
    userPresenceConfirmed: userPresenceState.confirmed,
    needsPresenceConfirmation: userPresenceState.needsConfirmation,
    confirmPresence,
    pauseUpdates,
    resetPresenceTimer,
  }
}
